import { Matrix4, Vector3 } from 'three';
import { calculateCartographicBasis, toCartographic } from './Math';
/**
 * Oriented Bounding Box implementation
 */
export class OBB {
    constructor(center = new Vector3(), halfAxes = new Matrix4()) {
        Object.defineProperty(this, "isOBB", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "center", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "halfAxes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.isOBB = true;
        this.center = center;
        this.halfAxes = halfAxes;
    }
    /**
     * Create a new oriented bounding box with the same internal values
     */
    clone() {
        return new OBB(this.center.clone(), this.halfAxes.clone());
    }
    /**
     * Copy another OBB into this one
     */
    copy(obb) {
        this.center.copy(obb.center);
        this.halfAxes.copy(obb.halfAxes);
    }
    /**
     * Create a new OBB from a set of face center positions
     *
     * Assumes that every 2 consecutive faces are opposite each other
     */
    setFromFaceCenters(faceCenters) {
        const basisChange = [];
        this.center.set(0, 0, 0);
        for (let i = 0; i < faceCenters.length; i += 2) {
            const c0 = faceCenters[i];
            const c1 = faceCenters[i + 1];
            this.center.add(c0);
            this.center.add(c1);
            const halfAxis = c1.clone().sub(c0).divideScalar(2);
            basisChange.push(halfAxis);
        }
        this.center.divideScalar(6);
        this.halfAxes.makeBasis(basisChange[0], basisChange[1], basisChange[2]);
        return this;
    }
    /**
     * Create a new OBB from a geolocated sphere.
     *
     * @param sphere
     */
    setFromGeoSphere(sphere) {
        // Orient the axes to the surface of the globe
        const x = new Vector3();
        const y = new Vector3();
        const z = new Vector3();
        calculateCartographicBasis(toCartographic(sphere.center), y, x, z);
        // Scale the axes to fit the bounding volume
        x.multiplyScalar(sphere.radius);
        y.multiplyScalar(sphere.radius);
        z.multiplyScalar(sphere.radius);
        // Create the halfAxes matrix
        const halfAxes = new Matrix4().makeBasis(x, y, z);
        this.center.copy(sphere.center);
        this.halfAxes.copy(halfAxes);
        return this;
    }
    /**
     * Rotate the oriented bounding box
     */
    rotate(rotation) {
        const transform = new Matrix4();
        transform.makeRotationFromQuaternion(rotation);
        this.halfAxes.multiplyMatrices(transform, this.halfAxes);
    }
    /**
     * Get the inward planes from the faces of the bounding volumes
     *
     * Opposing planes are listed consecutively in the returned array
     */
    getInwardPlanes() {
        const basisVectors = [new Vector3(), new Vector3(), new Vector3()];
        this.halfAxes.extractBasis(basisVectors[0], basisVectors[1], basisVectors[2]);
        // Include the negative axes for the opposite facing planes
        // Opposite face planes are consecutive in the array
        const axes = [];
        for (const axis of basisVectors) {
            axes.push(axis);
            axes.push(axis.clone().negate());
        }
        // Calculate position and normal
        const planes = axes.map(axis => {
            const position = this.center.clone().add(axis);
            const normal = this.center.clone().sub(position).normalize();
            return { position, normal };
        });
        return planes;
    }
    /**
     * Calculate the corners of the OBB to generate a triangle mesh
     */
    getTriangleMeshVertexArray() {
        const x = new Vector3();
        const y = new Vector3();
        const z = new Vector3();
        this.halfAxes.extractBasis(x, y, z);
        const vertices = [
            this.center.clone().sub(x).sub(y).add(z),
            this.center.clone().add(x).sub(y).add(z),
            this.center.clone().sub(x).add(y).add(z),
            this.center.clone().add(x).add(y).add(z),
            this.center.clone().sub(x).sub(y).sub(z),
            this.center.clone().add(x).sub(y).sub(z),
            this.center.clone().sub(x).add(y).sub(z),
            this.center.clone().add(x).add(y).sub(z),
        ];
        const indices = [
            //Top
            2, 6, 7, 7, 3, 2,
            //Bottom
            5, 4, 0, 0, 1, 5,
            //Left
            6, 2, 0, 0, 4, 6,
            //Right
            1, 3, 7, 7, 5, 1,
            //Front
            0, 2, 3, 3, 1, 0,
            //Back
            7, 6, 4, 4, 5, 7,
        ];
        return { vertices, indices };
    }
}
